import styles from "./mainPage.module.css";
import { tWithKeyAttr as t } from "../../i18nHelper";
import reportsIcon from "../../images/reports_icon.svg";
import calendar from "../../images/calendar.png";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  testUserEmail,
  therapistLinks,
  getMainPageTexts,
  mobileWidth,
  TABLE_IDS,
} from "../../utils/constants";
import venchy from "../../images/questionnaire_doctor.png";
import { getPrintDate, replaceVariables } from "../../utils/utils";
import Slider from "../../components/slider/slider";
import Pagination from "../../components/pagination/pagination";
import useMediaQuery from "../../hooks/useMediaQuery";
import { useTablePermissions } from "../../hooks/useTablePermissions";
import {
  // useGetMediasQuery,
  useGetStatusesQuery,
  useGetAvailableOpenTreatmentPlansByTherapistGuidMutation,
  useLazyGetMainPageInfoQuery,
  useGetRRMListMutation,
  useGetQuestionnaiersButtonsMutation,
  useGetPendingReportsQuery,
  useLazyGetUnSeenAppointmentsProgramQuery,
  useGetAdminMessagesQuery,
} from "../../services/api/user.api";
import { useAppSelector } from "../../services/hooks";
import { useActions } from "../../services/hooks/useActions";
import {
  mainPageUpdatesSelector,
  // mediasSelector,
  statusesSelector,
  therapistWithUsersSelector,
  allTreatmentPlansSelector,
  userSelector,
  usersWithOutTherapistSelector,
  groupedUsersByFakeCompanyIdSelector,
  currentInquirySelector,
  currentTreatmentPlanSelector,
  RRMListSelector,
  questionnairWithoutUserButtonsSelector,
  UserQuestionnairSiteLinkSelector,
  unSeenAppointmentsProgramsSelector,
  pendingReportAppointmentsSelector,
  pendingReportInquiriesSelector,
} from "../../services/selectors/user.selectors";
import {
  IAdminMessage,
  IDatedText,
  ITreatmentPlan,
  TLimits,
  TQuestionnaireButton,
} from "../../types/types";
import {
  TGroupedUsersWithoutTherapistByFakeCompanyId,
  TTherapistWithUsers,
  TUsersWithOutTherapist,
} from "../../services/types/user.types";
import { useNavigate } from "react-router-dom";
import ListPagination from "../../components/listPagination/listPagination";
import FilterButtonMainPage from "../../components/filterButton/filterButtonMainPage";
import Loader from "../../components/loader/loader";
import TreatmentDocument from "../../components/document/treatmentDocument";
import FilterButtonTreatmentPlan from "../../components/filterButton/filterButtonTreatmentPlan";
import isEqual from "lodash/isEqual";
import { unSeenChatMessagesSelector } from "../../services/selectors/chat.selector";
import i18n from "../../i18n";
import InquiryHeaderWithTooltip from "../../components/inquiryHeaderWithTooltip/inquiryHeaderWithTooltip";
import groupIcon from "../../images/group_users_icon_black.svg";
import { useInquiryDocumentPanel } from "../../contexts/InquiryDocumentPanelContext";
import { getServerStatusText } from "../../utils/therapistDocuments";

function MainPage() {
  const navigate = useNavigate();
  const { openInquiryDocument } = useInquiryDocumentPanel();

  const { setCurrentTreatmentPlan } = useActions();
  const inquiriesRefs = useRef<Map<number, HTMLLIElement | null>>(new Map());
  const treatmentPlansRefs = useRef<Map<number, HTMLLIElement | null>>(
    new Map()
  );

  // Get table permissions (isLoading needed: effect must re-run when permissions load)
  const { hasPermission, isReadOnly, isLoading: isPermissionsLoading } = useTablePermissions();

  const therapist = useAppSelector(userSelector);
  const serverStatuses = useAppSelector(statusesSelector);
  const treatmentPlans = useAppSelector(allTreatmentPlansSelector)?.filter(
    (plan) => plan.appointmentsProgram.statusId
  );

  // const medias = useAppSelector(mediasSelector);
  const updates = useAppSelector(mainPageUpdatesSelector) as IDatedText[];
  const therapistWithUsers = useAppSelector(therapistWithUsersSelector);
  const currentInquiry = useAppSelector(currentInquirySelector);
  const currentTreatmentPlan = useAppSelector(currentTreatmentPlanSelector);

  const usersWithOutTherapist = useAppSelector(usersWithOutTherapistSelector);
  const groupedUsersByFakeCompanyId = useAppSelector(
    groupedUsersByFakeCompanyIdSelector
  );
  const RRMListDefault = useAppSelector(RRMListSelector);
  const questionnaireButtons = useAppSelector(
    questionnairWithoutUserButtonsSelector
  ).filter((button) => !button.isUsers);
  const questionnaireSiteLink = useAppSelector(
    UserQuestionnairSiteLinkSelector
  );

  const unSeenAppointmentsPrograms = useAppSelector(
    unSeenAppointmentsProgramsSelector
  );
  const unSeenChatMessages = useAppSelector(unSeenChatMessagesSelector);
  const pendingReportInquiries = useAppSelector(pendingReportInquiriesSelector);
  const pendingReportAppointments = useAppSelector(
    pendingReportAppointmentsSelector
  );


  const unSeenItemsCount: Record<string, { num: number; color: string }> = {
    treatmentPlan: { num: unSeenAppointmentsPrograms, color: "#da1e28" },
    messages: { num: unSeenChatMessages ?? 0, color: "blue" },
    calendar: { num: 0, color: "" },
    pendingReports: {
      num: pendingReportAppointments.length + pendingReportInquiries.length,
      color: "#da1e28",
    },
  };

  const isTestUser = therapist?.email.toLowerCase() === testUserEmail;

  // Fetch admin messages from API
  const { data: adminMessages } = useGetAdminMessagesQuery(
    { companyId: therapist.companyId },
    { skip: !therapist.companyId }
  );

  // Transform admin messages to IDatedText format, fallback to first hardcoded message only if empty
  const displayUpdates: IDatedText[] = useMemo(() => {
    const activeMessages = adminMessages?.filter((msg: IAdminMessage) => msg.StatusId !== 0) || [];

    if (activeMessages.length === 0) {
      // Fallback to first hardcoded message only
      const fallbackTexts = getMainPageTexts();
      return fallbackTexts.length > 0 ? [fallbackTexts[0]] : [];
    }

    return activeMessages.map((msg: IAdminMessage, index: number) => {
      // Clean text: remove HTML tags, decode entities, trim whitespace
      const cleanText = (str: string | undefined | null): string => {
        if (!str) return '';
        return str
          .replace(/&lt;/gi, '<')
          .replace(/&gt;/gi, '>')
          .replace(/&amp;/gi, '&')
          .replace(/\r\n/g, '\n') // Normalize line endings
          .replace(/\r/g, '\n')
          .trim();
      };

      return {
        id: msg.Id || index + 1,
        text: [cleanText(msg.Title), cleanText(msg.Desc)]
          .filter(Boolean)
          .join('\n'),
        date: new Date(msg.UpdatedAt),
        createdUserName: msg.CreatedUserName ?? ''
      };
    });
  }, [adminMessages]);

  useGetPendingReportsQuery();
  useGetStatusesQuery();
  // useGetMediasQuery();

  const [getTreatmentPlan, { isError: isTreatmentPlansError }] =
    useGetAvailableOpenTreatmentPlansByTherapistGuidMutation();
  const [getMainPageInfo, { isLoading: isMainPageInfoLoading, isError }] =
    useLazyGetMainPageInfoQuery();
  const [getUnreadAppointmentsProgram] =
    useLazyGetUnSeenAppointmentsProgramQuery();
  const [getRRMList] = useGetRRMListMutation();
  const [getQuestionnaiersButtons] = useGetQuestionnaiersButtonsMutation();
  const mobile = useMediaQuery(mobileWidth);

  const [activeIndex, setActiveIndex] = useState(0);
  const [treatmentDocumentToShow, setTreatmentDocumentToShow] =
    useState<ITreatmentPlan | null>(null);
  const [openTreatmentDocument, setOpenTreatmentDocument] = useState(false);

  const [treatmentPlansToshow, setTreatmentPlansToshow] = useState<
    ITreatmentPlan[] | null | undefined
  >(treatmentPlans);

  const [therapistWithUsersToShow, setTherapistWithUsersToShow] = useState<
    TTherapistWithUsers[] | undefined
  >(undefined);
  const [
    groupedUsersByFakeCompanyIdToShow,
    setGroupedUsersByFakeCompanyIdToShow,
  ] = useState<TGroupedUsersWithoutTherapistByFakeCompanyId | undefined>(
    undefined
  );
  const [therapistWithUsersLimits, setTherapistWithUsersLimits] =
    useState<TLimits>({ start: 1, end: 1 });
  const [
    groupedUsersByFakeCompanyIdLimit,
    setGroupedUsersByFakeCompanyIdLimit,
  ] = useState<
    { FakeComapnyId: number | string; limit: TLimits }[] | undefined
  >(undefined);
  const [treatmentPlanLimits, setTreatmentPlanLimits] = useState<TLimits>({
    start: 1,
    end: 1,
  });
  //limit inquiries to 7
  const limitNumber = 7;
  const isLoading = isMainPageInfoLoading || !therapist.id;

  useEffect(() => {
    if (therapist.id) {
      // Only call getMainPageInfo if user has permission for at least one inquiry table
      const hasNewInquiriesPermission = hasPermission(TABLE_IDS.NEW_INQUIRIES);
      const hasMyInquiriesPermission = hasPermission(TABLE_IDS.MY_INQUIRIES);
      
      if (hasNewInquiriesPermission || hasMyInquiriesPermission) {
        getMainPageInfo();
      }
      
      getTreatmentPlan({
        companyId: therapist.companyId,
        therapistsGuid: therapist.guid,
      });
      getUnreadAppointmentsProgram();

      const interval = setInterval(() => {
        // Recalculate permissions on each interval tick in case they changed
        const hasNewInquiriesPermissionInterval = hasPermission(TABLE_IDS.NEW_INQUIRIES);
        const hasMyInquiriesPermissionInterval = hasPermission(TABLE_IDS.MY_INQUIRIES);
        
        if (hasNewInquiriesPermissionInterval || hasMyInquiriesPermissionInterval) {
          getMainPageInfo();
        }
      }, 60000);

      return () => clearInterval(interval); // Cleanup on unmount
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapist.id, therapist.companyId, therapist.guid, isPermissionsLoading]);

  useEffect(() => {
    if (therapistWithUsersToShow && therapistWithUsersToShow.length > 0) {
      setTherapistWithUsersLimits((prev) => ({
        ...prev,
        end:
          (therapistWithUsersLimits.end > 1) ? prev.end :
            limitNumber < therapistWithUsersToShow.length
              ? limitNumber
              : therapistWithUsersToShow.length,
      }));
    }

    if (
      groupedUsersByFakeCompanyIdToShow &&
      Object.keys(groupedUsersByFakeCompanyIdToShow).length > 0
    ) {
      const transformedGroups = Object.entries(groupedUsersByFakeCompanyId).map(
        ([key, items]) => {
          const usersGroup = groupedUsersByFakeCompanyIdLimit?.find(
            (li) => li.FakeComapnyId.toString() === key
          );

          return {
            FakeComapnyId: key,
            limit: {
              start: usersGroup?.limit.start ?? 1,
              end: usersGroup?.limit.end
                ? usersGroup.limit.end
                : limitNumber < (items?.length ?? 0)
                  ? limitNumber
                  : items?.length ?? 0,
            },
          };
        }
      );
      setGroupedUsersByFakeCompanyIdLimit(transformedGroups);
    }

    if (treatmentPlansToshow && treatmentPlansToshow.length > 0) {
      setTreatmentPlanLimits((prev) => ({
        ...prev,
        end:
          (treatmentPlanLimits.end > 1) ? prev.end :
            limitNumber < treatmentPlans.length
              ? limitNumber
              : treatmentPlans.length,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    groupedUsersByFakeCompanyIdToShow,
    therapistWithUsersToShow,
    treatmentPlansToshow,
  ]);

  useEffect(() => {
    setTherapistWithUsersToShow(therapistWithUsers ?? undefined);
    if (
      !Object.values(groupedUsersByFakeCompanyId).every(
        (arr) => arr.length === 0
      )
    ) {
      setGroupedUsersByFakeCompanyIdToShow(groupedUsersByFakeCompanyId);
    } else {
      setGroupedUsersByFakeCompanyIdToShow(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [therapistWithUsers, groupedUsersByFakeCompanyId]);

  useEffect(() => {
    if (treatmentPlans && !treatmentPlansToshow) {
      setTreatmentPlansToshow(treatmentPlans);
    }
  }, [treatmentPlans, treatmentPlansToshow]);


  useEffect(() => {
    if (!RRMListDefault?.length) {
      getRRMList({ type: 1, companyId: therapist.companyId });
    }
    if (!questionnaireButtons?.length) {
      getQuestionnaiersButtons({ type: 1, companyId: therapist.companyId });
    }
    const timeout = setTimeout(() => {
      let targetRef: HTMLLIElement | null | undefined;
      if (currentInquiry) {
        targetRef = inquiriesRefs.current.get(currentInquiry);
      } else if (currentTreatmentPlan) {
        targetRef = treatmentPlansRefs.current.get(currentTreatmentPlan);
      }
      if (targetRef) {
        targetRef.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
    return () => clearTimeout(timeout); // Cleanup timeout if the component unmounts

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showDocument = (doc: TTherapistWithUsers | TUsersWithOutTherapist) => {
    openInquiryDocument(doc);
    setOpenTreatmentDocument(false);
  };

  const showTreatmentDocument = (plan: ITreatmentPlan) => {
    setCurrentTreatmentPlan(plan.appointmentsProgram.id);
    setTreatmentDocumentToShow(plan);
    setOpenTreatmentDocument(true);
  };

  const setFilterToShow = (
    users: TTherapistWithUsers[] | TUsersWithOutTherapist[] | undefined,
    fakeCompanyId?: string
  ) => {
    if (fakeCompanyId) {
      setGroupedUsersByFakeCompanyIdToShow((prev) => {
        const updatedState = {
          ...(prev || {}),
          [fakeCompanyId]: users ? [...users] : undefined,
        };

        return updatedState as TGroupedUsersWithoutTherapistByFakeCompanyId;
      });
    } else {
      setTherapistWithUsersToShow(users as TTherapistWithUsers[]);
    }
  };

  const setFilterTreatmentToShow = (
    treatments: ITreatmentPlan[] | undefined
  ) => {
    if (!isEqual(treatmentPlansToshow, treatments))
      setTreatmentPlansToshow(treatments);
  };

  const onQuestionnairButtonClick = (button: TQuestionnaireButton) => {
    if (questionnaireSiteLink) {
      const url = new URL(`${questionnaireSiteLink}/${button.companyName}`);
      url.searchParams.append("therapistGuid", String(therapist.guid));
      url.searchParams.append(
        "questionnaireCompaniesTypeId",
        String(button.questionnaireCompaniesTypeId)
      );
      window.open(url.toString(), "_blank", "noopener,noreferrer");
    }
  };

  return (
    <section className={styles.section} dir={i18n.dir()} data-testid='home-section'>
      <ul className={`${styles.tabs}`}>
        {therapistLinks.map((tab) => (
          <li //change to Link when needed
            key={tab.title}
            // to={`/${tab.value}`}
            className={`${styles.tab}
              } ${isTestUser && "hover"}`}
            onClick={() => {
              navigate(`/${tab.value}`);
            }}
          >
            <img src={tab.img} alt={tab.title} className={styles.tabsImg} />
            <p className={styles.tabText}>{t(tab.title)}</p>
            {unSeenItemsCount[tab.value] &&
              unSeenItemsCount[tab.value].num > 0 && (
                <span
                  className={styles.unSeenItemsCount}
                  style={{
                    backgroundColor: unSeenItemsCount[tab.value].color || "",
                  }}
                >
                  {unSeenItemsCount[tab.value].num}
                </span>
              )}
          </li>
        ))}
      </ul>
      <div className={styles.content}>
        {treatmentDocumentToShow && (
          <TreatmentDocument
            treatmentPlan={treatmentDocumentToShow}
            openDocument={openTreatmentDocument}
            // This table lists plans across the company that the therapist may take,
            // not plans already assigned to them, so the patient notes stay hidden.
            hideProgramComments
            onClose={() => {
              setOpenTreatmentDocument(false);
              setCurrentTreatmentPlan(null);
            }}
          />
        )}
        <article className={styles.applications}>
          <div className={styles.home__section}>
            <h4 className={styles.home__sectionTitle}>
              {t("MAIN_PAGE_UPDATES_TITLE")}
            </h4>
            <div className={styles.notifications}>
              <div className={styles.notifications__venchy}>
                <img
                  src={venchy}
                  className={styles.notifications__img}
                  alt={t("IMAGE_ALT_VENCHY")}
                />
                <div>
                  <p className={styles.notifications__name}>{displayUpdates[activeIndex].createdUserName || t("VENCHY_ALT")}</p>
                  <p className={styles.notifications__date}>
                    {displayUpdates[activeIndex] && getPrintDate(displayUpdates[activeIndex].date)}
                  </p>
                </div>
              </div>
              <Slider
                items={displayUpdates.map((u: IDatedText) => ({
                  ...u,
                  text: replaceVariables(u.text.startsWith("MAIN_PAGE_") ? t(u.text) : u.text, therapist),
                }))}
                className={styles.notifications__text}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
              />
              {displayUpdates.length > 1 && (
                <button
                  className={styles.nextBtn}
                  onClick={() =>
                    setActiveIndex((prevIndex) =>
                      prevIndex === displayUpdates.length - 1 ? 0 : prevIndex + 1
                    )
                  }
                />
              )}
              {displayUpdates.length > 1 && (
                <button
                  className={styles.prevBtn}
                  onClick={() =>
                    setActiveIndex((prevIndex) =>
                      prevIndex === 0 ? displayUpdates.length - 1 : prevIndex - 1
                    )
                  }
                />
              )}
            </div>
          </div>
          <Pagination
            items={displayUpdates.map((u) => u.text)}
            onClick={(index: number) => setActiveIndex(index)}
            activeIndex={activeIndex}
          />
        </article>
        <div className={`${styles.questionnairButton_container}`}>
          {questionnaireButtons &&
            questionnaireButtons.length !== 0 &&
            questionnaireButtons.map((button, index) => (
              <button
                key={index}
                className={`${styles.questionnairButton_btn} hover`}
                type="button"
                onClick={() => onQuestionnairButtonClick(button)}
              >
                {button.buttonText}
              </button>
            ))}
        </div>
        <>
          {hasPermission(TABLE_IDS.NEW_INQUIRIES) && (isLoading && (!groupedUsersByFakeCompanyIdToShow || Object.keys(groupedUsersByFakeCompanyIdToShow).length === 0) ? (
            <article className={styles.home__section}>
              <h4 className={styles.home__sectionTitle}>
                {t("MAIN_PAGE_NEW_INQUIRIES_DEFAULT")}
              </h4>
              <ul className={styles.inquiries}>
                <div className={styles.text}>
                  <Loader />
                </div>
              </ul>
            </article>
          ) : groupedUsersByFakeCompanyIdToShow &&
            Object.keys(groupedUsersByFakeCompanyIdToShow).length > 0 ? (
            Object.entries(groupedUsersByFakeCompanyIdToShow).map(
              ([key, items]) => {
                const limit = groupedUsersByFakeCompanyIdLimit?.find(
                  (li) =>
                    (li.FakeComapnyId === "null" &&
                      !groupedUsersByFakeCompanyId?.[
                        key as keyof typeof groupedUsersByFakeCompanyId
                      ]?.[0]?.fakeCompanyId) ||
                    Number(li.FakeComapnyId) ===
                    groupedUsersByFakeCompanyId?.[
                      key as keyof typeof groupedUsersByFakeCompanyId
                    ]?.[0]?.fakeCompanyId
                );

                return (
                  <div key={key}>
                    <article className={styles.home__section}>
                      <h4 className={styles.home__sectionTitle}>
                        {key === "null"
                          ? t("MAIN_PAGE_NEW_INQUIRIES_DEFAULT")
                          : `${t("MAIN_PAGE_NEW_INQUIRIES_PREFIX")}  ${groupedUsersByFakeCompanyId?.[
                            key as keyof typeof groupedUsersByFakeCompanyId
                          ]?.[0]?.fakeCompanyName ?? ""
                          }`}
                      </h4>
                      <ul className={styles.inquiries}>
                        {groupedUsersByFakeCompanyId &&
                          Object.entries(groupedUsersByFakeCompanyId).length >=
                          1 &&
                          groupedUsersByFakeCompanyId["null"].length ? (
                          <li
                            className={`${styles.inquiry} ${styles.inquiry_contact} ${styles.inquiryHeader}`}
                          >
                            <InquiryHeaderWithTooltip
                              className={`${styles.inquiryCell} ${styles.inquiryNumber}`}
                              text={t("MAIN_PAGE_INQUIRY_NUMBER")}
                            />
                            <InquiryHeaderWithTooltip
                              className={`${styles.inquiryCell} ${styles.inquiryDate}`}
                              text={t("PROGRAM_DATES_POPUP_INQUIRY_DATE")}
                            />{" "}
                            <InquiryHeaderWithTooltip
                              className={`${styles.inquiryCell} ${styles.inquiryName}`}
                              text={t("MAIN_PAGE_INQUIRY_NAME_SHORT")}
                            />
                            <InquiryHeaderWithTooltip
                              className={`${styles.inquiryCell} ${styles.inquiryCycle}`}
                              text={t("MAIN_PAGE_INQUIRY_CYCLE")}
                            />
                            <InquiryHeaderWithTooltip
                              className={`${styles.inquiryCell} ${styles.inquiryContent}`}
                              text={t("MAIN_PAGE_INQUIRY_SUBJECT_SHORT")}
                            />
                            <div
                              className={`${styles.inquiryCell} ${styles.inquiryStatus}`}
                            >
                              {items && (
                                <FilterButtonMainPage
                                  documentsToFilter={
                                    groupedUsersByFakeCompanyId[
                                    key as keyof typeof groupedUsersByFakeCompanyId
                                    ]
                                  }
                                  setDocumentsToFilter={setFilterToShow}
                                  fakeCompanyId={key}
                                />
                              )}
                            </div>
                          </li>
                        ) : isLoading ? (
                          <div className={styles.text}>
                            <Loader />
                          </div>
                        ) : (
                          <p className={styles.text}>
                            {isError
                              ? t("MAIN_PAGE_TREATMENT_ERROR")
                              : t("MAIN_PAGE_NO_NEW_INQUIRIES")}
                          </p>
                        )}

                        {items &&
                          items.length > 0 &&
                          items.map((i, index) => {
                            return (
                              limit &&
                              index >= limit.limit.start - 1 &&
                              index < limit?.limit.end && (
                                <li
                                  key={i.num}
                                  className={`${styles.inquiry} ${styles.inquiry_contact
                                    } 
                                  ${index % 2 === 0 && styles.inquiry_colored}
                                   ${i.num === currentInquiry
                                      ? styles.inquiry_selected
                                      : ""
                                    }
                                   ${isReadOnly(TABLE_IDS.NEW_INQUIRIES) ? styles.inquiry_readOnly : ""                          }`}
                          ref={(el) => inquiriesRefs.current.set(i.num, el)}
                          onClick={
                            !mobile && !isReadOnly(TABLE_IDS.NEW_INQUIRIES) ? () => showDocument(i) : undefined
                          }
                        >
                          {i.appointmentsId &&
                            i.appointmentsTypeId === 1 ? (
                            <img
                              src={calendar}
                              alt="isOnline"
                              className={`${styles.icon} ${styles.online_button}  ${styles.icon_animation}`}
                            />
                          ) : (
                            i.appointmentsTypeId === 2 && (
                              <img
                                src={calendar}
                                alt="isOnline"
                                className={`${styles.icon} ${styles.online_button}  ${styles.icon_blue}`}
                              />
                            )
                          )}
                                  <p
                                    className={`${styles.inquiryCell} ${styles.inquiryNumber}`}
                                  >
                                    {i.num}
                                  </p>
                                  <p
                                    className={`${styles.inquiryCell} ${styles.inquiryDate}`}
                                  >
                                    {`${getPrintDate(new Date(i.timestamp))}`}
                                  </p>
                                  <p
                                    className={`${styles.inquiryCell} ${styles.inquiryName}`}
                                  >
                                    {i.firstName || ""} {i.lastName || ""}
                                  </p>
                                  <p
                                    className={`${styles.inquiryCell} ${styles.inquiryCycle}`}
                                  >
                                    {i.cycle}
                                  </p>
                                  <p
                                    className={`${styles.inquiryCell} ${styles.inquiryCategory}`}
                                  >
                                    {i.questionnaireCompaniesType}
                                  </p>
                                  <button
                                    className={`${styles.inquiryCell} button button_white ${styles.btn_take}`}
                                    onClick={
                                      mobile && !isReadOnly(TABLE_IDS.NEW_INQUIRIES) ? () => showDocument(i) : undefined
                                    }
                                  >
                                    {t("MAIN_PAGE_TAKE_BUTTON")}
                                  </button>
                                  <span
                                    className={styles.inquiryRRM}
                                    style={{
                                      backgroundColor:
                                        RRMListDefault.find(
                                          (item) =>
                                            item.rrmseq === i.generalRRMSEQ
                                        )?.color || "",
                                    }}
                                  />
                                </li>
                              )
                            );
                          })}
                      </ul>
                    </article>
                    {items && items.length > 0 && limit && (
                      <ListPagination
                        limits={limit.limit}
                        total={items.length || 0}
                        setLimits={setGroupedUsersByFakeCompanyIdLimit}
                        step={limitNumber}
                        groupKey={limit.FakeComapnyId}
                      />
                    )}
                  </div>
                );
              }
            )
          ) : (
            <article className={styles.home__section}>
              <h4 className={styles.home__sectionTitle}>
                {t("MAIN_PAGE_NEW_INQUIRIES_DEFAULT")}
              </h4>
              <ul className={styles.inquiries}>
                <p className={styles.text}>
                  {isError
                    ? t("MAIN_PAGE_TREATMENT_ERROR")
                    : t("MAIN_PAGE_NO_NEW_INQUIRIES")}
                </p>
              </ul>
            </article>
          ))}
        </>
        {hasPermission(TABLE_IDS.MY_INQUIRIES) && (
        <article className={styles.home__section}>
          <h4 className={styles.home__sectionTitle}>
            {t("MAIN_PAGE_MY_INQUIRIES_TITLE")}
          </h4>

          <ul className={styles.inquiries}>
            {therapistWithUsers && therapistWithUsers.length > 0 ? (
              <li
                className={`${styles.inquiry} ${styles.inquiry_my_contact} ${styles.inquiryHeader}`}
              >
                <InquiryHeaderWithTooltip
                  className={`${styles.inquiryCell} ${styles.inquiryNumber}`}
                  text={t("MAIN_PAGE_INQUIRY_NUMBER")}
                />
                <InquiryHeaderWithTooltip
                  className={`${styles.inquiryCell} ${styles.inquiryDate}`}
                  text={t("PROGRAM_DATES_POPUP_INQUIRY_DATE")}
                />
                <InquiryHeaderWithTooltip
                  className={`${styles.inquiryCell} ${styles.inquiryName}`}
                  text={t("MAIN_PAGE_INQUIRY_NAME_SHORT")}
                />
                <InquiryHeaderWithTooltip
                  className={`${styles.inquiryCell} ${styles.inquiryCycle}`}
                  text={t("MAIN_PAGE_INQUIRY_CYCLE")}
                />
                <InquiryHeaderWithTooltip
                  className={`${styles.inquiryCell} ${styles.inquiryContent}`}
                  text={t("MAIN_PAGE_INQUIRY_SUBJECT_SHORT")}
                />

                <div
                  className={`${styles.inquiryCell} ${styles.inquiryStatus}`}
                >
                  {therapistWithUsers && (
                    <FilterButtonMainPage
                      documentsToFilter={therapistWithUsers}
                      setDocumentsToFilter={setFilterToShow}
                    />
                  )}
                </div>
              </li>
            ) : isLoading ? (
              <div className={styles.text}>
                <Loader />
              </div>
            ) : (
              <p className={styles.text}>
                {isError
                  ? t("MAIN_PAGE_TREATMENT_ERROR")
                  : t("MAIN_PAGE_NO_NEW_INQUIRIES")}
              </p>
            )}
            {therapistWithUsersToShow &&
              therapistWithUsersToShow.map(
                (i, index) =>
                  index >= therapistWithUsersLimits.start - 1 &&
                  index < therapistWithUsersLimits.end && (
                    <li
                      key={i.num}
                      className={`${styles.inquiry} ${styles.inquiry_my_contact}
                     ${i.num === currentInquiry ? styles.inquiry_selected : ""}
                     ${index % 2 === 0 && styles.inquiry_colored}
                     ${isReadOnly(TABLE_IDS.MY_INQUIRIES) ? styles.inquiry_readOnly : ""}`}
                      onClick={!mobile && !isReadOnly(TABLE_IDS.MY_INQUIRIES) ? () => showDocument(i) : undefined}
                      ref={(el) => inquiriesRefs.current.set(i.num, el)}
                    >
                      {i.appointmentsId ? (
                        !i.appointmentsActive ? (
                          <img
                            src={calendar}
                            alt="isOnline"
                            className={`${styles.icon} ${styles.online_button} ${styles.icon_disabled}`}
                          />
                        ) : i.appointmentsTypeId === 1 ? (
                          <img
                            src={calendar}
                            alt="isOnline"
                            className={`${styles.icon} ${styles.online_button} ${styles.icon_animation}`}
                          />
                        ) : (
                          (i.appointmentsTypeId === 2 ||
                            !i.appointmentsTypeId) && (
                            <img
                              src={calendar}
                              alt="isOnline"
                              className={`${styles.icon} ${styles.online_button} ${styles.icon_blue}`}
                            />
                          )
                        )
                      ) : (
                        <></>
                      )}
                      <p
                        className={`${styles.inquiryCell} ${styles.inquiryNumber}`}
                      >
                        {i.num}
                      </p>
                      <p
                        className={`${styles.inquiryCell} ${styles.inquiryDate}`}
                      >
                        {`${getPrintDate(new Date(i.timestamp))}`}
                      </p>
                      <p
                        className={`${styles.inquiryCell} ${styles.inquiryName}`}
                      >
                        {i.firstName || ""} {i.lastName || ""}
                      </p>
                      <p
                        className={`${styles.inquiryCell} ${styles.inquiryCycle}`}
                      >
                        {i.cycle}
                      </p>
                      <p
                        className={`${styles.inquiryCell} ${styles.inquiryCategory}`}
                      >
                        {i.questionnaireCompaniesType}
                      </p>
                      <p
                        className={`${styles.inquiryCell} ${styles.inquiryAction}`}
                      >
                        {i.therapistActionId ? (
                          <img
                            src={reportsIcon}
                            alt={t("PROGRAM_DATES_POPUP_REPORT_ACTION")}
                            className={`${styles.icon}`}
                            title={t("PROGRAM_DATES_POPUP_REPORT_ACTION")}
                          />
                        ) : (
                          "--"
                        )}
                      </p>
                      <p
                        className={`${styles.inquiryCell} ${styles.inquiryStatus}`}
                        onClick={mobile && !isReadOnly(TABLE_IDS.MY_INQUIRIES) ? () => showDocument(i) : undefined}
                      >
                        {getServerStatusText(i.status, serverStatuses)}
                      </p>
                      <span
                        className={styles.inquiryRRM}
                        style={{
                          backgroundColor:
                            RRMListDefault.find(
                              (item) => item.rrmseq === i.generalRRMSEQ
                            )?.color || "",
                        }}
                      />
                    </li>
                  )
              )}
          </ul>
        </article>
        )}
        {hasPermission(TABLE_IDS.MY_INQUIRIES) && therapistWithUsersToShow && therapistWithUsersToShow.length > 0 && (
          <ListPagination
            limits={therapistWithUsersLimits}
            total={therapistWithUsersToShow?.length || 0}
            setLimits={setTherapistWithUsersLimits}
            step={limitNumber}
          />
        )}
        {hasPermission(TABLE_IDS.TREATMENT_PLANS) && (
        <article className={styles.home__section}>
          <h4 className={styles.home__sectionTitle}>
            {t("MAIN_PAGE_NEW_TREATMENT_PLANS_TITLE")}
          </h4>
          <ul className={styles.inquiries}>
            {treatmentPlans && treatmentPlans.length > 0 ? (
              <li
                className={`${styles.inquiry} ${styles.inquiry_contact} ${styles.inquiryHeader}`}
              >
                <InquiryHeaderWithTooltip
                  className={`${styles.inquiryCell} ${styles.inquiryNumber}`}
                  text={t("MAIN_PAGE_TREATMENT_NUMBER")}
                />
                <InquiryHeaderWithTooltip
                  className={`${styles.inquiryCell} ${styles.inquiryDate}`}
                  text={t("MAIN_PAGE_TREATMENT_DATE_SHORT")}
                />
                <InquiryHeaderWithTooltip
                  className={`${styles.inquiryCell} ${styles.inquiryName}`}
                  text={t("MAIN_PAGE_INQUIRY_NAME_SHORT")}
                />
                <InquiryHeaderWithTooltip
                  className={`${styles.inquiryCell} ${styles.inquiryCycle}`}
                  text={t("MAIN_PAGE_TREATMENT_NAME")}
                />
                <InquiryHeaderWithTooltip
                  className={`${styles.inquiryCell} ${styles.inquiryContent}`}
                  text={t("MAIN_PAGE_TREATMENT_METHOD_SHORT")}
                />
                <div
                  className={`${styles.inquiryCell} ${styles.inquiryStatus}`}
                >
                  <FilterButtonTreatmentPlan
                    documentsToFilter={treatmentPlans}
                    setDocumentsToFilter={setFilterTreatmentToShow}
                  />
                </div>
              </li>
            ) : isLoading ? (
              <div className={styles.text}>
                <Loader />
              </div>
            ) : (
              <p className={styles.text}>
                {isTreatmentPlansError
                  ? t("MAIN_PAGE_TREATMENT_ERROR")
                  : t("MAIN_PAGE_NO_TREATMENT_PLANS")}
              </p>
            )}
            {treatmentPlansToshow &&
              treatmentPlansToshow.map(
                (i, index) =>
                  index >= treatmentPlanLimits.start - 1 &&
                  index < treatmentPlanLimits.end && (
                    <li
                      key={index}
                      className={`${styles.inquiry} ${styles.inquiry_contact} ${index % 2 === 0 && styles.inquiry_colored
                        }
                      ${i.appointmentsProgram.id === currentTreatmentPlan
                          ? styles.inquiry_selected
                          : ""
                        }
                      ${isReadOnly(TABLE_IDS.TREATMENT_PLANS) ? styles.inquiry_readOnly : ""}`}
                      onClick={
                        !mobile && !isReadOnly(TABLE_IDS.TREATMENT_PLANS)
                          ? () => {
                            showTreatmentDocument(i);
                          }
                          : undefined
                      }
                      ref={(el) =>
                        treatmentPlansRefs.current.set(
                          i.appointmentsProgram.id,
                          el
                        )
                      }
                    >
                      <p
                        className={`${styles.inquiryCell} ${styles.inquiryNumber}`}
                      >
                        {i.appointmentsProgram.id}
                      </p>
                      <p
                        className={`${styles.inquiryCell} ${styles.inquiryDate}`}
                      >
                        {`${getPrintDate(
                          new Date(i.appointmentsProgram.createDate)
                        )}`}
                      </p>
                      <p
                        className={`${styles.inquiryCell} ${styles.inquiryName}`}
                      >
                        {i.treatmentGroupName ? (
                          <span className={styles.groupNameContainer}>
                            <img src={groupIcon} alt="group" className={styles.groupIcon} />
                            {i.treatmentGroupName}
                          </span>
                        ) : (
                          i.user?.fullName || ""
                        )}
                      </p>
                      <p
                        className={`${styles.inquiryCell} ${styles.inquiryCycle}`}
                      >
                        {i.appointmentsProgram.name || ""}
                      </p>
                      <p
                        className={`${styles.inquiryCell} ${styles.inquiryCategory}`}
                      >
                        {i.mediaName}
                      </p>
                      <button
                        className={`${styles.inquiryCell} button button_white ${styles.btn_take}`}
                        onClick={
                          mobile && !isReadOnly(TABLE_IDS.TREATMENT_PLANS)
                            ? () => {
                              showTreatmentDocument(i);
                            }
                            : undefined
                        }
                      >
                        {t("TREATMENT_DOCUMENT_BUTTON_TAKE")}
                      </button>
                      <span className={styles.inquiryRRM} />
                    </li>
                  )
              )}
          </ul>
        </article>
        )}
        {hasPermission(TABLE_IDS.TREATMENT_PLANS) && treatmentPlans && treatmentPlans.length > 0 && (
          <ListPagination
            limits={treatmentPlanLimits}
            total={treatmentPlans?.length || 0}
            setLimits={setTreatmentPlanLimits}
            step={limitNumber}
          />
        )}
      </div>
    </section>
  );
}

export default MainPage;
